import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BarChart3,
  CreditCard,
  DollarSign,
  FileText,
  HeartPulse,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  type LucideIcon,
  Megaphone,
  Moon,
  MessageSquare,
  Package,
  PackagePlus,
  Palette,
  Plus,
  RefreshCw,
  Server,
  Settings,
  Shield,
  ShoppingCart,
  Store,
  Sun,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useShop } from '@/contexts/ShopContext';
import { useTheme } from '@/contexts/ThemeContext';
import { usePermissions } from '@/hooks/usePermissions';
import { adminPath, ownerPath } from '@/lib/hosts';

export type CommandActionGroup = 'navigation' | 'create' | 'shop' | 'system' | 'admin';

export interface CommandActionDescriptor {
  /** Identifiant stable pour test/key React. */
  id: string;
  /** Libellé visible utilisateur. */
  label: string;
  /** Icône Lucide. */
  icon: LucideIcon;
  /** Groupe d'affichage dans la palette. */
  group: CommandActionGroup;
  /** Mots-clés pour recherche fuzzy (palette cmdk). */
  keywords?: string[];
  /** Affichage shortcut visuel (ex: "G S", "⌘ N"). */
  shortcut?: string;
  /** Si défini : navigation. Mutuellement exclusif avec `run`. */
  href?: string;
  /** Si défini : action exécutable. */
  run?: () => Promise<void> | void;
  /** Permission requise (cf. usePermissions). */
  permission?: string;
  /** Réservé aux admins. */
  adminOnly?: boolean;
  /** Compteur affiché en badge (ex: nb ruptures). */
  badge?: number;
  /** Hint contextuel (ex: "12 en rupture"). */
  hint?: string;
  /** Surface ce descripteur dans le widget Dashboard. */
  quickAction?: boolean;
  /** Toast affiché en cas de succès d'une action `run`. */
  successMessage?: string;
}

interface UseCommandActionsOptions {
  /** Nombre de produits en rupture — pour badge "Réappro stock". */
  outOfStockCount?: number;
  /** Nombre de crédits en retard — pour badge "Relancer impayé". */
  overdueCount?: number;
  /** Callback de fermeture (palette/sheet) après run. */
  onAfterRun?: () => void;
}

/**
 * Source unique de vérité pour les actions de la CommandPalette, du widget
 * Dashboard QuickActions et du sheet mobile.
 *
 * Chaque action est filtrée par permissions + role admin. Les actions
 * exécutables (`run`) reçoivent un toast feedback automatique.
 *
 * @example
 *   const { actions, quickActions, runAction } = useCommandActions({
 *     outOfStockCount: 12,
 *     overdueCount: 3,
 *     onAfterRun: () => setOpen(false),
 *   });
 */
export function useCommandActions(options: UseCommandActionsOptions = {}) {
  const { outOfStockCount, overdueCount, onAfterRun } = options;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['navigation', 'dashboard', 'common']);
  const { isAdmin, logout } = useAuth();
  const { currentShop } = useShop();
  const { resolvedTheme, setTheme } = useTheme();
  const { can } = usePermissions();

  const refreshData = useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const allActions = useMemo<CommandActionDescriptor[]>(() => {
    const restockHint =
      typeof outOfStockCount === 'number'
        ? t('dashboard:quick_restock_hint', {
            count: outOfStockCount,
            defaultValue: `${outOfStockCount} en rupture`,
          })
        : undefined;
    const chaseHint =
      typeof overdueCount === 'number'
        ? t('dashboard:quick_chase_hint', {
            count: overdueCount,
            defaultValue: `${overdueCount} SMS prêt`,
          })
        : undefined;

    return [
      // ────────── QUICK ACTIONS (dashboard widget) ──────────
      {
        id: 'sale.new',
        label: t('dashboard:quick_new_sale', { defaultValue: 'Nouvelle vente' }),
        icon: ShoppingCart,
        group: 'create',
        href: '/pos',
        keywords: ['nouvelle vente', 'pos', 'caisse', 'sell'],
        shortcut: '⌘ N',
        hint: t('dashboard:quick_new_sale_hint', { defaultValue: 'Ouvrir le POS' }),
        permission: 'sales.create',
        quickAction: true,
      },
      {
        id: 'stock.restock',
        label: t('dashboard:quick_restock', { defaultValue: 'Réappro stock' }),
        icon: PackagePlus,
        group: 'navigation',
        href: '/stock/alerts',
        keywords: ['stock', 'rupture', 'reappro', 'alerts'],
        shortcut: 'R',
        hint: restockHint,
        badge: outOfStockCount,
        permission: 'stock.view',
        quickAction: true,
      },
      {
        id: 'credits.chase',
        label: t('dashboard:quick_chase', { defaultValue: 'Relancer impayé' }),
        icon: MessageSquare,
        group: 'navigation',
        href: '/finances',
        keywords: ['credit', 'impaye', 'sms', 'relance'],
        shortcut: 'L',
        hint: chaseHint,
        badge: overdueCount,
        permission: 'reports.view',
        quickAction: true,
      },
      {
        id: 'product.new',
        label: t('dashboard:quick_new_product', { defaultValue: 'Nouveau produit' }),
        icon: Plus,
        group: 'create',
        href: '/products/create',
        keywords: ['nouveau produit', 'product'],
        shortcut: 'P',
        hint: t('dashboard:quick_new_product_hint', { defaultValue: 'Mode express' }),
        permission: 'products.create',
        quickAction: true,
      },
      {
        id: 'report.today',
        label: t('dashboard:quick_report', { defaultValue: 'Rapport du jour' }),
        icon: FileText,
        group: 'navigation',
        href: '/finances',
        keywords: ['rapport', 'pdf', 'export'],
        shortcut: 'E',
        hint: t('dashboard:quick_report_hint', { defaultValue: 'Exporter PDF' }),
        permission: 'reports.view',
        quickAction: true,
      },

      // ────────── NAVIGATION ──────────
      {
        id: 'nav.dashboard',
        label: t('navigation:dashboard'),
        icon: LayoutDashboard,
        group: 'navigation',
        href: '/dashboard',
        keywords: ['home', 'accueil'],
        shortcut: 'G D',
        permission: 'reports.view',
      },
      {
        id: 'nav.products',
        label: t('navigation:products'),
        icon: Package,
        group: 'navigation',
        href: '/products',
        keywords: ['produit'],
        shortcut: 'G P',
        permission: 'products.view',
      },
      {
        id: 'nav.categories',
        label: t('navigation:categories'),
        icon: Layers,
        group: 'navigation',
        href: '/categories',
        keywords: ['categorie'],
        permission: 'categories.view',
      },
      {
        id: 'nav.brands',
        label: t('navigation:brands'),
        icon: Package,
        group: 'navigation',
        href: '/brands',
        keywords: ['marque'],
        permission: 'brands.view',
      },
      {
        id: 'nav.sales',
        label: t('navigation:sales'),
        icon: DollarSign,
        group: 'navigation',
        href: '/sales',
        keywords: ['vente'],
        shortcut: 'G S',
        permission: 'sales.view',
      },
      {
        id: 'nav.customers',
        label: t('navigation:customers'),
        icon: Users,
        group: 'navigation',
        href: '/customers',
        keywords: ['client'],
        shortcut: 'G C',
        permission: 'customers.view',
      },
      {
        id: 'nav.employees',
        label: t('navigation:employees'),
        icon: Users,
        group: 'navigation',
        href: '/employees',
        keywords: ['employe', 'staff'],
        shortcut: 'G E',
        permission: 'employees.view',
      },
      {
        id: 'nav.stock',
        label: t('navigation:stock'),
        icon: BarChart3,
        group: 'navigation',
        href: '/stock',
        keywords: ['inventaire'],
        shortcut: 'G K',
        permission: 'stock.view',
      },
      {
        id: 'nav.shops',
        label: t('navigation:shops'),
        icon: Store,
        group: 'navigation',
        href: '/shops',
        keywords: ['boutique'],
      },
      {
        id: 'nav.settings',
        label: t('navigation:settings'),
        icon: Settings,
        group: 'navigation',
        href: '/settings/profile',
        keywords: ['parametre', 'preferences'],
        shortcut: 'G ,',
      },
      {
        id: 'nav.billing',
        label: t('navigation:subscription'),
        icon: CreditCard,
        group: 'navigation',
        href: '/subscription',
        keywords: ['facturation', 'plan', 'abonnement'],
        permission: 'subscriptions.view',
      },

      // ────────── CREATE (alternatifs) ──────────
      {
        id: 'create.customer',
        label: t('common:new_customer', { defaultValue: 'Nouveau client' }),
        icon: Plus,
        group: 'create',
        href: '/customers',
        keywords: ['nouveau client', 'add customer'],
        permission: 'customers.create',
      },
      {
        id: 'create.employee',
        label: t('common:new_employee', { defaultValue: 'Nouvel employé' }),
        icon: Plus,
        group: 'create',
        href: '/employees',
        keywords: ['nouvel employe', 'add staff'],
        permission: 'employees.create',
      },

      // ────────── SYSTEM (executable) ──────────
      {
        id: 'system.refresh',
        label: t('common:action_refresh', { defaultValue: 'Actualiser les données' }),
        icon: RefreshCw,
        group: 'system',
        run: refreshData,
        keywords: ['refresh', 'reload', 'actualiser', 'sync'],
        successMessage: t('common:refresh_success', { defaultValue: 'Données actualisées' }),
      },
      {
        id: 'system.toggleTheme',
        label:
          resolvedTheme === 'dark'
            ? t('common:action_theme_light', { defaultValue: 'Passer en clair' })
            : t('common:action_theme_dark', { defaultValue: 'Passer en sombre' }),
        icon: resolvedTheme === 'dark' ? Sun : Moon,
        group: 'system',
        run: toggleTheme,
        keywords: ['theme', 'dark', 'light', 'sombre', 'clair', 'mode'],
      },
      {
        id: 'system.logout',
        label: t('common:logout', { defaultValue: 'Se déconnecter' }),
        icon: LogOut,
        group: 'system',
        run: handleLogout,
        keywords: ['logout', 'signout', 'deconnexion'],
      },

      // ────────── ADMIN ──────────
      {
        id: 'admin.dashboard',
        label: 'Admin Dashboard',
        icon: Shield,
        group: 'admin',
        href: '/admin',
        keywords: ['administration', 'tableau de bord'],
        shortcut: 'g a',
        adminOnly: true,
      },
      {
        id: 'admin.users',
        label: 'Admin Users',
        icon: Users,
        group: 'admin',
        href: '/users',
        keywords: ['utilisateurs', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.shops',
        label: 'Admin Shops',
        icon: Store,
        group: 'admin',
        href: '/shops',
        keywords: ['boutiques', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.subscriptions',
        label: 'Admin Subscriptions',
        icon: CreditCard,
        group: 'admin',
        href: '/subscriptions',
        keywords: ['abonnements', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.plans',
        label: 'Admin Plans',
        icon: Layers,
        group: 'admin',
        href: '/plans',
        keywords: ['plans', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.finance',
        label: 'Admin Finance',
        icon: DollarSign,
        group: 'admin',
        href: '/finance',
        keywords: ['finance', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.announcements',
        label: 'Admin Announcements',
        icon: Megaphone,
        group: 'admin',
        href: '/announcements',
        keywords: ['annonces', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.sync',
        label: 'Admin Sync',
        icon: RefreshCw,
        group: 'admin',
        href: '/sync',
        keywords: ['synchronisation', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.logs',
        label: 'Admin System Logs',
        icon: Server,
        group: 'admin',
        href: '/system/logs',
        keywords: ['logs', 'audit', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.support',
        label: 'Admin Support Tickets',
        icon: LifeBuoy,
        group: 'admin',
        href: '/support',
        keywords: ['tickets', 'support', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.quotes',
        label: 'Admin Quote Requests',
        icon: FileText,
        group: 'admin',
        href: '/quotes',
        keywords: ['devis', 'quotes', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.health',
        label: 'Admin System Health',
        icon: HeartPulse,
        group: 'admin',
        href: '/system/health',
        keywords: ['health', 'sante', 'admin'],
        adminOnly: true,
      },
      {
        id: 'admin.aiStyles',
        label: 'Admin AI Styles',
        icon: Palette,
        group: 'admin',
        href: '/ai/document-styles',
        keywords: ['styles', 'ia', 'ai', 'admin'],
        adminOnly: true,
      },
    ];
  }, [handleLogout, outOfStockCount, overdueCount, refreshData, resolvedTheme, t, toggleTheme]);

  const visibleActions = useMemo(() => {
    return allActions
      .filter((action) => {
        if (action.adminOnly && !isAdmin) return false;
        if (action.permission && !can(action.permission)) return false;
        return true;
      })
      .map((action) => {
        // Resolve href to the host-aware path so links work in local dev (which
        // prefixes /owner/* and /admin/*) AND in prod (where each subdomain
        // serves routes from its root). Without this, every QuickAction Link
        // and runAction navigate misses the registered route in local mode and
        // ends up in the catch-all -> DefaultRedirect -> bounce to /dashboard.
        if (!action.href) return action;
        const resolved = action.adminOnly ? adminPath(action.href) : ownerPath(action.href);
        return { ...action, href: resolved };
      });
  }, [allActions, can, isAdmin]);

  const groups = useMemo(() => {
    const acc: Record<CommandActionGroup, CommandActionDescriptor[]> = {
      navigation: [],
      create: [],
      shop: [],
      system: [],
      admin: [],
    };
    for (const action of visibleActions) {
      acc[action.group].push(action);
    }
    return acc;
  }, [visibleActions]);

  const quickActions = useMemo(
    () => visibleActions.filter((action) => action.quickAction),
    [visibleActions],
  );

  const runAction = useCallback(
    async (action: CommandActionDescriptor) => {
      try {
        if (action.run) {
          await action.run();
          if (action.successMessage) {
            toast.success(action.successMessage);
          }
        } else if (action.href) {
          navigate(action.href);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Action a échoué';
        toast.error(message);
      } finally {
        onAfterRun?.();
      }
    },
    [navigate, onAfterRun],
  );

  return {
    /** Toutes les actions visibles (filtrées par perms et admin). */
    actions: visibleActions,
    /** Actions groupées par catégorie. */
    groups,
    /** Actions à surfacer dans le Dashboard widget Quick Actions. */
    quickActions,
    /** Exécute une action (nav OU run) avec toast feedback. */
    runAction,
    /** Boutique courante (utile pour intégration shops switch). */
    currentShop,
  };
}
